import { useState } from 'react';
import { notification } from 'antd';
import { useTelegram } from '../../context/TelegramContext';
import { ReferralLinkPayload, ReferralReward } from '../../types/referral';
import { copyToClipboard } from '../../utils/clipboard';
import { openExternalLink } from '../../utils/telegramLink';
import { formatCurrency } from '../../utils/format';

interface ReferralSectionProps {
    payload: ReferralLinkPayload | null;
}

const CopyIcon = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
        <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
        <path d="M5 15V6a2 2 0 0 1 2-2h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const ShareIcon = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true" style={{ marginRight: 6 }}>
        <path d="M4 12.5l15.5-7.5-5 15-2.8-5.7L4 12.5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
);

/** "72 soat" reads worse than "3 kun"; whole days are shown as days, anything else as hours. */
const freeTimeText = (hours: number) => (hours % 24 === 0 && hours >= 24 ? `${hours / 24} kun` : `${hours} soat`);

const rewardText = (reward: ReferralReward) => {
    switch (reward.kind) {
        case 'free_time':
            return `${freeTimeText(reward.value)} bepul obuna`;
        case 'percent':
            return `${reward.value}% chegirma`;
        default:
            return `${formatCurrency(reward.value)} chegirma`;
    }
};

/**
 * The sentence under the title. Names what the subscriber earns per friend, then what the friend gets;
 * when the program grants nothing to either side the generic line is used.
 */
const descriptionText = (payload: ReferralLinkPayload) => {
    const parts: string[] = [];

    if (payload.referrer_reward) {
        parts.push(`Har bir taklif qilingan do'stingiz uchun ${rewardText(payload.referrer_reward)} olasiz.`);
    }

    if (payload.invitee_reward) {
        parts.push(`Do'stingiz esa birinchi to'lovga ${rewardText(payload.invitee_reward)} oladi.`);
    }

    return parts.length > 0 ? parts.join(' ') : "Havolani do'stlaringizga yuboring va bonuslarga ega bo'ling.";
};

/** `https://t.me/Bot?start=ref_X` is shown as `t.me/Bot?start=ref_X` — the scheme is noise on a phone. */
const displayUrl = (url: string) => url.replace(/^https?:\/\//, '');

const ReferralSection = ({ payload }: ReferralSectionProps) => {
    const { webApp } = useTelegram();
    const [copied, setCopied] = useState(false);

    // Nothing to show → the whole section stays out of the DOM (house rule: no empty block). Covers a
    // failed load, a disabled program, and a user outside the program's audience alike.
    if (!payload?.available || !payload.url) {
        return null;
    }

    const url = payload.url;
    const shareUrl = payload.share_url || `https://t.me/share/url?url=${encodeURIComponent(url)}`;

    const handleCopy = async () => {
        const ok = await copyToClipboard(url);

        if (ok) {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
            webApp?.HapticFeedback?.notificationOccurred?.('success');
            notification.success({ message: 'Havola nusxalandi', placement: 'bottom', duration: 2 });
        } else {
            webApp?.HapticFeedback?.notificationOccurred?.('error');
            notification.warning({
                message: "Nusxalab bo'lmadi",
                description: "Havolani qo'lda belgilab nusxalang yoki \"Ulashish\" tugmasidan foydalaning.",
                placement: 'bottom',
            });
        }
    };

    const handleShare = () => {
        openExternalLink(webApp, shareUrl);
        webApp?.HapticFeedback?.impactOccurred?.('light');
    };

    return (
        <div className="referral-section">
            <div className="referral-card">
                <div className="referral-card__title">Do'stlaringizni taklif qiling</div>
                <div className="referral-card__description">{descriptionText(payload)}</div>

                <button type="button" className="referral-link" onClick={handleCopy} aria-label="Havolani nusxalash">
                    <span className="referral-link__url">{displayUrl(url)}</span>
                    <span className="referral-link__icon">
                        <CopyIcon />
                    </span>
                </button>

                <div className="referral-card__actions">
                    <button type="button" className="referral-btn referral-btn--copy" onClick={handleCopy}>
                        {copied ? 'Nusxalandi' : 'Nusxalash'}
                    </button>
                    <button type="button" className="referral-btn referral-btn--share" onClick={handleShare}>
                        <ShareIcon />
                        Ulashish
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReferralSection;
